import type {
  SQResponse,
  SOResponse,
  SQListResponse,
  SOListResponse,
  SQNumberResponse,
  SONumberResponse,
  CreateSQRequest,
  CreateSORequest,
  UpdateSQRequest,
  UpdateSORequest,
  UpdateSQStatusRequest,
  UpdateSOStatusRequest,
  UpdateSOItemStatusRequest,
  SQFilter,
  SOFilter,
  SQStatus,
  SOStatus,
  Customer,
} from '../types/salesorder';
import { get, post, put, patch, del, buildUrl } from './apiClient';

const SQ_API_BASE = '/api/v1/sales/quotations';
const SO_API_BASE = '/api/v1/sales/orders';

// ============================================================================
// Sales Quotation CRUD
// ============================================================================

export async function createSQ(request: CreateSQRequest): Promise<SQResponse> {
  return post<SQResponse>(SQ_API_BASE, request);
}

export async function getSQ(id: string): Promise<SQResponse> {
  return get<SQResponse>(`${SQ_API_BASE}/${id}`);
}

export async function listSQs(filter?: SQFilter): Promise<SQListResponse> {
  return get<SQListResponse>(buildUrl(SQ_API_BASE, filter));
}

export async function updateSQ(id: string, request: UpdateSQRequest): Promise<SQResponse> {
  return put<SQResponse>(`${SQ_API_BASE}/${id}`, request);
}

export async function updateSQStatus(id: string, status: SQStatus): Promise<void> {
  await patch(`${SQ_API_BASE}/${id}/status`, { status } as UpdateSQStatusRequest);
}

export async function deleteSQ(id: string): Promise<void> {
  await del(`${SQ_API_BASE}/${id}`);
}

export async function acceptQuotation(id: string): Promise<SOResponse> {
  return post<SOResponse>(`${SQ_API_BASE}/${id}/accept`);
}

// ============================================================================
// Sales Order CRUD
// ============================================================================

export async function createSO(request: CreateSORequest): Promise<SOResponse> {
  return post<SOResponse>(SO_API_BASE, request);
}

export async function getSO(id: string): Promise<SOResponse> {
  return get<SOResponse>(`${SO_API_BASE}/${id}`);
}

export async function listSOs(filter?: SOFilter): Promise<SOListResponse> {
  return get<SOListResponse>(buildUrl(SO_API_BASE, filter));
}

export async function updateSO(id: string, request: UpdateSORequest): Promise<SOResponse> {
  return put<SOResponse>(`${SO_API_BASE}/${id}`, request);
}

export async function updateSOStatus(id: string, status: SOStatus): Promise<void> {
  await patch(`${SO_API_BASE}/${id}/status`, { status } as UpdateSOStatusRequest);
}

export async function updateSOItemStatus(
  soId: string,
  lineNo: number,
  request: UpdateSOItemStatusRequest
): Promise<void> {
  await patch(`${SO_API_BASE}/${soId}/items/${lineNo}/status`, request);
}

export async function deleteSO(id: string): Promise<void> {
  await del(`${SO_API_BASE}/${id}`);
}

// ============================================================================
// Customer API
// ============================================================================

export async function getCustomers(): Promise<Customer[]> {
  return get<Customer[]>(`${SQ_API_BASE}/customers`);
}

// ============================================================================
// Utility API
// ============================================================================

export async function getNextSQNumber(): Promise<string> {
  const data = await get<SQNumberResponse>(`${SQ_API_BASE}/next-number`);
  return data.sq_number;
}

export async function getNextSONumber(): Promise<string> {
  const data = await get<SONumberResponse>(`${SO_API_BASE}/next-number`);
  return data.so_number;
}

// ============================================================================
// Helper Functions
// ============================================================================

export function formatCurrency(amount: number, currency: string = 'TWD'): string {
  const currencySymbols: Record<string, string> = {
    TWD: 'NT$',
    USD: '$',
    EUR: '\u20AC',
    JPY: '\u00A5',
    CNY: '\u00A5',
  };
  const symbol = currencySymbols[currency] || currency;
  return `${symbol} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function calculateItemAmount(quantity: number, unitPrice: number): number {
  return quantity * unitPrice;
}

export function calculateSQTotals(
  items: Array<{ quantity: number; unit_price: number }>
): { subtotal: number } {
  const subtotal = items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  return { subtotal };
}

export function calculateSOTotals(
  items: Array<{ quantity: number; unit_price: number }>,
  taxRate: number = 0.05
): { subtotal: number; taxAmount: number; grandTotal: number } {
  const subtotal = items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  const taxAmount = subtotal * taxRate;
  const grandTotal = subtotal + taxAmount;
  return { subtotal, taxAmount, grandTotal };
}

export function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export function formatDateRange(from: string, until: string): string {
  return `${formatDate(from)} ~ ${formatDate(until)}`;
}

export function isQuotationExpired(validUntil: string): boolean {
  if (!validUntil) return false;
  return new Date(validUntil) < new Date();
}

export function getDaysUntilExpiry(validUntil: string): number {
  if (!validUntil) return 0;
  const now = new Date();
  const expiry = new Date(validUntil);
  const diffTime = expiry.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}
